import {
  Box,
  Button,
  LinearProgress,
  Paper,
  Popper,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useOnboarding } from "./OnboardingContext";
import type { OnboardingPlacement, OnboardingStep } from "./steps";
import { resolveStepPlacement } from "./steps";
import { useOnboardingScrollLock } from "./useOnboardingScrollLock";

type HighlightRect = { top: number; left: number; width: number; height: number };
type ViewportSize = { width: number; height: number };

function findTarget(targetId: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(`[data-onboarding-id="${targetId}"]`);
  for (const el of nodes) {
    const rect = el.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) return el;
  }
  return nodes[0] ?? null;
}

function stepTargetIds(step: OnboardingStep): string[] {
  return [step.targetId, ...(step.extraTargetIds ?? [])];
}

function rectFromEl(el: HTMLElement): HighlightRect {
  const r = el.getBoundingClientRect();
  return {
    top: r.top - 4,
    left: r.left - 4,
    width: r.width + 8,
    height: r.height + 8,
  };
}

function readViewport(): ViewportSize {
  return { width: window.innerWidth, height: window.innerHeight };
}

/** Approximate coachmark tip height + gap, so scroll leaves room for it. */
const TIP_RESERVE = 220;

function scrollPaddingForPlacement(placement: OnboardingPlacement = "bottom") {
  const pad = 24;
  if (placement.startsWith("top")) {
    return { topPad: TIP_RESERVE, bottomPad: pad };
  }
  if (placement.startsWith("bottom")) {
    return { topPad: pad, bottomPad: TIP_RESERVE };
  }
  // Left/right tips sit beside the target — no extra vertical scroll room.
  return { topPad: pad, bottomPad: pad };
}

function targetElementsNeedScroll(
  els: HTMLElement[],
  placement: OnboardingPlacement = "bottom",
): boolean {
  if (els.length === 0) return false;

  const rects = els.map((el) => el.getBoundingClientRect());
  const top = Math.min(...rects.map((r) => r.top));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  const vh = window.innerHeight;
  const { topPad, bottomPad } = scrollPaddingForPlacement(placement);

  return top < topPad || bottom > vh - bottomPad;
}

/** Scroll the window so every target is visible when possible. */
function scrollTargetsIntoView(
  els: HTMLElement[],
  placement: OnboardingPlacement = "bottom",
) {
  if (els.length === 0) return;
  if (!targetElementsNeedScroll(els, placement)) return;

  const rects = els.map((el) => el.getBoundingClientRect());
  const top = Math.min(...rects.map((r) => r.top));
  const bottom = Math.max(...rects.map((r) => r.bottom));
  const vh = window.innerHeight;
  const { topPad, bottomPad } = scrollPaddingForPlacement(placement);

  const unionHeight = bottom - top;
  const absoluteTop = top + window.scrollY;
  // Prefer centering the target in the band that leaves tip room.
  const usable = vh - topPad - bottomPad;
  if (unionHeight <= usable) {
    const targetScroll = absoluteTop - topPad - (usable - unionHeight) / 2;
    window.scrollTo({ top: Math.max(0, targetScroll), behavior: "auto" });
  } else if (placement.startsWith("top")) {
    // Tall target: keep its bottom in view with room for a tip above.
    window.scrollTo({
      top: Math.max(0, absoluteTop + unionHeight - (vh - bottomPad)),
      behavior: "auto",
    });
  } else {
    // Bottom or side placement: keep the top of the target in view.
    window.scrollTo({ top: Math.max(0, absoluteTop - topPad), behavior: "auto" });
  }
}

function isInsideCoachmark(node: EventTarget | null): boolean {
  return node instanceof Element && Boolean(node.closest("[data-onboarding-coachmark]"));
}

function findStepTargetElements(step: OnboardingStep): HTMLElement[] {
  return stepTargetIds(step)
    .map((id) => findTarget(id))
    .filter((el): el is HTMLElement => el != null);
}

function highlightScrollFingerprint(highlights: HighlightRect[]): string {
  return highlights
    .map((h) => `${Math.round(h.top)}:${Math.round(h.left)}:${Math.round(h.width)}:${Math.round(h.height)}`)
    .join("|");
}

function scheduleScrollReady(setScrollReady: (ready: boolean) => void) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => setScrollReady(true));
  });
}

function performStepScroll(step: OnboardingStep, placement: OnboardingPlacement): boolean {
  if (step.fullOverlay) {
    window.scrollTo({ top: 0, behavior: "auto" });
    return true;
  }
  const ids = stepTargetIds(step);
  const els = findStepTargetElements(step);
  if (els.length < ids.length) return false;
  scrollTargetsIntoView(els, placement);
  return true;
}

export default function OnboardingCoachmark() {
  const { active, step, stepNumber, totalSteps, skip, next, mobile } = useOnboarding();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [highlights, setHighlights] = useState<HighlightRect[]>([]);
  const [viewport, setViewport] = useState<ViewportSize>(() =>
    typeof window === "undefined" ? { width: 0, height: 0 } : readViewport(),
  );
  /** Hide spotlight + tip once the user starts the indicated action. */
  const [dismissed, setDismissed] = useState(false);
  /** Defer scroll lock until programmatic scroll for this step has settled. */
  const [scrollReady, setScrollReady] = useState(false);
  const scrolledStepKeyRef = useRef<string | null>(null);

  const routeScrollKey = `${step?.id ?? ""}:${location.pathname}${location.search}`;
  const [trackedRouteScrollKey, setTrackedRouteScrollKey] = useState(routeScrollKey);
  if (trackedRouteScrollKey !== routeScrollKey) {
    setTrackedRouteScrollKey(routeScrollKey);
    setScrollReady(false);
    scrolledStepKeyRef.current = null;
  }

  const fullOverlay = Boolean(step?.fullOverlay);
  const stepPlacement = step ? resolveStepPlacement(step, mobile) : "bottom";
  const targetIds = useMemo(() => (step ? stepTargetIds(step) : []), [step]);
  const showSpotlight =
    !dismissed && !fullOverlay && Boolean(anchorEl) && highlights.length > 0;
  const showPopperTip = !dismissed && !fullOverlay && Boolean(anchorEl);
  const showFallback = !dismissed && !fullOverlay && !anchorEl;
  const showFinale = !dismissed && fullOverlay;
  /** Only lock page scroll while the dimming overlay is on screen (not after dismiss / tip-only). */
  const shouldLockScroll = scrollReady && (showSpotlight || showFinale);
  useOnboardingScrollLock(shouldLockScroll, targetIds);

  const syncHighlights = useCallback(() => {
    if (!step || step.fullOverlay) {
      setAnchorEl(null);
      setHighlights([]);
      setViewport(readViewport());
      return [];
    }
    const ids = stepTargetIds(step);
    const els = ids
      .map((id) => findTarget(id))
      .filter((el): el is HTMLElement => el != null);
    const primary = findTarget(step.targetId) ?? els[0] ?? null;
    setAnchorEl(primary);
    setHighlights(els.map(rectFromEl));
    setViewport(readViewport());
    return els;
  }, [step]);

  useEffect(() => {
    setDismissed(false);
    setScrollReady(false);
    scrolledStepKeyRef.current = null;
  }, [step?.id]);

  useEffect(() => {
    if (dismissed) {
      setScrollReady(false);
    }
  }, [dismissed]);

  useLayoutEffect(() => {
    if (!active || dismissed || !step) return;

    const scrollKey = `${step.id}:${location.pathname}${location.search}:${highlights.length}:${Boolean(anchorEl)}:${highlightScrollFingerprint(highlights)}`;
    if (scrolledStepKeyRef.current === scrollKey) return;
    if (!performStepScroll(step, stepPlacement)) return;

    scrolledStepKeyRef.current = scrollKey;
    scheduleScrollReady(setScrollReady);
  }, [
    active,
    step,
    stepPlacement,
    dismissed,
    location.pathname,
    location.search,
    highlights,
    anchorEl,
  ]);

  useLayoutEffect(() => {
    if (!active || !step || dismissed || step.fullOverlay) {
      setAnchorEl(null);
      setHighlights([]);
      return;
    }
    let cancelled = false;
    let tries = 0;
    const tick = () => {
      if (cancelled) return;
      const els = syncHighlights() ?? [];
      const ids = stepTargetIds(step);
      if (els.length < ids.length && tries < 40) {
        tries += 1;
        window.setTimeout(tick, 100);
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [active, step, location.pathname, location.search, dismissed, syncHighlights]);

  // Keep cutouts locked to targets while scrolling / resizing / layout shifts.
  useEffect(() => {
    if (!active || dismissed || !step || step.fullOverlay) return;

    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        syncHighlights();
      });
    };

    sync();
    window.addEventListener("scroll", sync, true);
    window.addEventListener("resize", sync);

    const observers: ResizeObserver[] = [];
    const ids = stepTargetIds(step);
    for (const id of ids) {
      const el = findTarget(id);
      if (!el || typeof ResizeObserver === "undefined") continue;
      const ro = new ResizeObserver(sync);
      ro.observe(el);
      // Also watch parent layout (Teach page loading content).
      if (el.parentElement) ro.observe(el.parentElement);
      observers.push(ro);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", sync, true);
      window.removeEventListener("resize", sync);
      for (const ro of observers) ro.disconnect();
    };
  }, [active, dismissed, step, syncHighlights]);

  useEffect(() => {
    if (!active || dismissed || !step || step.fullOverlay) return;

    const retryScroll = () => {
      syncHighlights();
      const els = findStepTargetElements(step);
      if (els.length < stepTargetIds(step).length) return;
      if (!targetElementsNeedScroll(els, stepPlacement)) return;

      scrolledStepKeyRef.current = null;
      setScrollReady(false);
      if (!performStepScroll(step, stepPlacement)) return;
      scheduleScrollReady(setScrollReady);
    };

    // Remeasure after Teach async content settles and re-scroll if still off-screen.
    const timers = [350, 700, 1200].map((ms) => window.setTimeout(retryScroll, ms));
    return () => {
      for (const t of timers) window.clearTimeout(t);
    };
  }, [active, step?.id, dismissed, location.pathname, location.search, step, stepPlacement, syncHighlights]);

  // Dismiss tip when the user engages any highlighted control.
  useEffect(() => {
    if (!active || !step || dismissed || step.fullOverlay) return;

    const onInteract = (event: Event) => {
      if (isInsideCoachmark(event.target)) return;
      if (!(event.target instanceof Element)) return;
      const hit = targetIds.some(
        (id) => event.target instanceof Element && event.target.closest(`[data-onboarding-id="${id}"]`),
      );
      if (!hit) return;
      setDismissed(true);
    };

    document.addEventListener("pointerdown", onInteract, true);
    return () => {
      document.removeEventListener("pointerdown", onInteract, true);
    };
  }, [active, step, dismissed, targetIds]);

  if (!active || !step) return null;

  const progress = (stepNumber / totalSteps) * 100;
  const isLastStep = step.id === "teach-all-set";
  const nextLabel = isLastStep ? "Done" : "Next";
  const maskId = `onboarding-mask-${step.id}`;

  const tipBody = (
    <>
      <Typography fontWeight={700} sx={{ mb: 0.75 }}>
        {step.title}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: step.bullets?.length ? 1 : 1.5, whiteSpace: "pre-line" }}
      >
        {step.body}
      </Typography>
      {step.bullets?.length ? (
        <Box
          component="ul"
          sx={{
            m: 0,
            mb: 1.5,
            pl: 0,
            listStyle: "none",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {step.bullets.map((b) => (
            <Box
              key={b.emphasis}
              component="li"
              sx={{
                display: "flex",
                gap: 1,
                alignItems: "flex-start",
              }}
            >
              <Box component="span" aria-hidden sx={{ lineHeight: 1.4, flexShrink: 0 }}>
                {b.emoji}
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                {b.before}
                <Box component="span" sx={{ fontWeight: 700, color: "text.primary" }}>
                  {b.emphasis}
                </Box>
                {b.after}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        {!isLastStep ? (
          <Button size="small" onClick={skip} color="inherit">
            Skip tour
          </Button>
        ) : null}
        {step.showNext ? (
          <Button size="small" variant="contained" onClick={next}>
            {nextLabel}
          </Button>
        ) : null}
      </Stack>
    </>
  );

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          top: 0,
          zIndex: (t) => t.zIndex.modal + 2,
          pointerEvents: "none",
        }}
      >
        <LinearProgress
          variant="determinate"
          value={progress}
          sx={{
            height: 3,
            bgcolor: "transparent",
            "& .MuiLinearProgress-bar": { bgcolor: "primary.light" },
          }}
        />
      </Box>

      {showFinale && (
        <>
          <Box
            aria-hidden
            sx={{
              position: "fixed",
              inset: 0,
              zIndex: (t) => t.zIndex.modal,
              bgcolor: "rgba(0,0,0,0.45)",
              touchAction: "none",
            }}
          />
          <Paper
            data-onboarding-coachmark
            elevation={8}
            sx={{
              position: "fixed",
              top: 24,
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: (t) => t.zIndex.modal + 3,
              p: 2,
              maxWidth: 420,
              width: "calc(100% - 32px)",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            {tipBody}
          </Paper>
        </>
      )}

      {showSpotlight && viewport.width > 0 && (
        <>
          <Box
            aria-hidden
            component="svg"
            width={viewport.width}
            height={viewport.height}
            viewBox={`0 0 ${viewport.width} ${viewport.height}`}
            sx={{
              position: "fixed",
              left: 0,
              top: 0,
              width: viewport.width,
              height: viewport.height,
              zIndex: (t) => t.zIndex.modal,
              pointerEvents: "none",
              display: "block",
            }}
          >
            <defs>
              <mask id={maskId} maskUnits="userSpaceOnUse">
                <rect x="0" y="0" width={viewport.width} height={viewport.height} fill="white" />
                {highlights.map((h, i) => (
                  <rect
                    key={i}
                    x={h.left}
                    y={h.top}
                    width={h.width}
                    height={h.height}
                    rx="8"
                    ry="8"
                    fill="black"
                  />
                ))}
              </mask>
            </defs>
            <rect
              x="0"
              y="0"
              width={viewport.width}
              height={viewport.height}
              fill="rgba(0,0,0,0.45)"
              mask={`url(#${maskId})`}
            />
          </Box>
          {highlights.map((h, i) => (
            <Box
              key={i}
              aria-hidden
              sx={{
                position: "fixed",
                zIndex: (t) => t.zIndex.modal,
                pointerEvents: "none",
                borderRadius: 1,
                outline: "2px solid",
                outlineColor: "primary.main",
                top: h.top,
                left: h.left,
                width: h.width,
                height: h.height,
              }}
            />
          ))}
        </>
      )}

      <Popper
        open={showPopperTip}
        anchorEl={anchorEl}
        placement={stepPlacement}
        modifiers={[
          { name: "offset", options: { offset: [0, 12] } },
          { name: "preventOverflow", options: { padding: 8 } },
          {
            name: "flip",
            options: {
              padding: 8,
              fallbackPlacements:
                step.id === "teach-intro" && !mobile
                  ? ["right-start", "right", "bottom-start", "bottom"]
                  : undefined,
            },
          },
          { name: "eventListeners", options: { scroll: true, resize: true } },
        ]}
        sx={{ zIndex: (t) => t.zIndex.modal + 3 }}
      >
        <Paper
          data-onboarding-coachmark
          elevation={8}
          sx={{
            p: 2,
            maxWidth: 320,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          {tipBody}
        </Paper>
      </Popper>

      {showFallback && (
        <Paper
          data-onboarding-coachmark
          elevation={6}
          sx={{
            position: "fixed",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: (t) => t.zIndex.modal + 3,
            p: 2,
            maxWidth: 360,
            width: "calc(100% - 32px)",
          }}
        >
          {tipBody}
        </Paper>
      )}
    </>
  );
}
